import React, {useEffect, useRef, useState} from "react";
import {
  IAvailableFilter,
  IProductListFilterItem,
  ProductList,
  ProductListFilterItemBoolean,
  ProductListFilterItemRange,
  ProductListFilterItemSelect,
  ProductListFilterItemType,
  ProductListFilterItemValueType,
} from "@connox/typescript-library";
import {isSearchPage, useProductListContext} from "../productListProvider/ProductListProvider";
import ProductFilterSelect from "./ProductFilterSelect";
import ProductFilterBoolean from "./ProductFilterBoolean";
import ProductSortInput from "./ProductSortInput";
import ProductFilterRange from "./ProductFilterRange";
import {Trans, useTranslation} from "react-i18next";
import SelectedFilterOverview from "./SelectedFilterOverview";
import {useMediaQuery} from "react-responsive";
import Drawer from "../../lib/form/Drawer/Drawer";
import LoadingSpinner from "../../atoms/LoadingSpinner";
import ListItems from "../productList/ListItems";
import ProductFilterRangeAsSelect from "./ProductFilterRangeAsSelect";
import window from "../../lib/WindowExtended";

/**
 * Component to render the filter and sort buttons
 * @constructor
 */

const ProductListFilter: React.FC = () => {
  const {t} = useTranslation();
  const {state, dispatch} = useProductListContext();
  const filterOverviewRef = useRef<HTMLDivElement>(null);
  const filterHeadRef = useRef<HTMLDivElement>(null);
  const productListRef = useRef<HTMLDivElement>(null);

  const [isDrawerOpen, setDrawerOpen] = useState(false);
  const [mobileFilters, setMobileFilters] = useState<{[k: string]: ProductListFilterItemValueType}>({});
  const [isDirty, setIsDirty] = useState(false);
  const [openFilter, setOpenFilter] = useState(true);

  const isMobile = useMediaQuery({query: "(max-width: 940px)"});
  const productListStartPosition = (productListRef.current?.getBoundingClientRect().y || 400) + window.scrollY;

  const filterNumbers = (): number => {
    return Object.entries(state.filters).reduce((acc, [, value]) => {
      if (Array.isArray(value)) {
        return acc + value.length;
      } else {
        // case boolean or range filter are set
        return acc + 1;
      }
    }, 0);
  };

  const toggleFilter = (): void => {
    dispatch({type: "SHOW_FILTERS", payload: !state.showFilters});
    setDrawerOpen(!isDrawerOpen);
    setOpenFilter(!state.showFilters);

    // Scroll back to actual product list position
    if (!isMobile && window.scrollY > productListStartPosition + 450) {
      window.scrollBy(
        0,
        openFilter
          ? -Math.ceil((window.scrollY - productListStartPosition) / 4)
          : Math.ceil((window.scrollY - productListStartPosition) / 3),
      );
    }
  };

  // reset-section placeholder (necessary so that the product list always starts at the correct position)
  const FilterOverviewPlaceholder: React.FC = () => {
    const [productListPosition, setProductListPosition] = useState(filterOverviewRef.current?.clientHeight || 0);

    useEffect(() => {
      if (filterOverviewRef.current) {
        setProductListPosition(filterOverviewRef.current.clientHeight);
      }
    }, [JSON.stringify(filterNumbers()), filterOverviewRef.current?.clientHeight]);

    return (
      <div className="filter-remove__placeholder" style={{height: `${productListPosition}px`}}>
        {/* empty placeholder */}
      </div>
    );
  };

  const SearchHeadline: React.FC = () => {
    return (
      <div className="column-8 column-l-12">
        <h1 className="regular text-5 text-l-6 mb-4">
          <Trans
            i18nKey="search.searchHits--query--total"
            ns="translations"
            components={{
              s: <strong className="bold" />,
            }}
            values={{
              query: new URLSearchParams(window.location.search).get("q"),
              total: state.total,
            }}
          />
        </h1>
      </div>
    );
  };

  useEffect(() => {
    setOpenFilter(state.showFilters);
  }, [state.showFilters]);

  useEffect(() => {
    if (!state.loading) {
      setDrawerOpen(false);
      setIsDirty(false);

      if (openFilter) {
        dispatch({type: "SHOW_FILTERS", payload: true});
      }
    }
  }, [state.loading]);

  // Scroll to the top of the list after filtering/sorting
  useEffect(() => {
    if (window.scrollY > productListStartPosition) {
      window.scrollTo({
        top: productListStartPosition - (filterHeadRef.current?.clientHeight || 60),
        behavior: "smooth",
      });
    }
  }, [state.filters, state.orderBy]);

  useEffect(() => {
    if (isMobile) {
      setMobileFilters(state.filters);
    }
  }, [JSON.stringify(state.filters)]);

  useEffect(() => {
    const instance = state.instance;
    if (!instance || !isMobile) {
      return;
    }

    const instanceCopy = Object.assign(Object.create(Object.getPrototypeOf(instance)), instance) as ProductList;
    const filtersCopy = Object.assign({}, mobileFilters);
    // delete range filters keys if they are set
    Object.keys(mobileFilters).forEach((key) => {
      if (instance.filter.filterItems.get(key)?.type === ProductListFilterItemType.range) {
        delete filtersCopy[key];
      }
    });

    instanceCopy.filter.applyFilterValue(filtersCopy);
    instanceCopy.limit = 1;
    instanceCopy.offset = 0;

    const setRangeLimits = (filter: IAvailableFilter) => {
      dispatch({
        type: "SET_RANGE_FILTERS_VALUES",
        payload: {
          field: filter.field,
          value: {
            min: Math.floor(Number(filter.minValue ?? 0)),
            max: Math.ceil(Number(filter.maxValue ?? 0)),
          },
        },
      });
    };

    void instanceCopy.send().then((data) => {
      data.availableFilters.forEach((filter) => {
        if (filter.type === ProductListFilterItemType.range) {
          setRangeLimits(filter);
        }
      });
    });
  }, [JSON.stringify(mobileFilters)]);

  /**
   * Array of filter buttons, all of their corresponding `filterItem.type`
   */
  const filterItemArray = () => {
    const tempFilterItems: JSX.Element[] = [];
    // this is done to render boolean filters at the end of the list according to the ux requirement
    const tempBooleanFilterItems: JSX.Element[] = [];

    const {filtersToShow, filtersToHide, defaultFilter} = state;
    const pageCategoryTypeObject = window.dataLayer.find((elem) => elem["Page.CategoryTypeId"] !== undefined);
    const isVariantsCategoryType =
      (pageCategoryTypeObject ? pageCategoryTypeObject["Page.CategoryTypeId"] : undefined) === "100075";

    if (state.instance) {
      const filterItemsUnsorted = Array.from(state.instance.filter.filterItems.values()).filter(
        (value) =>
          value.values.size &&
          value.count &&
          (!filtersToShow.length || filtersToShow.includes(value.field)) &&
          (!filtersToHide.length || !filtersToHide.includes(value.field)) &&
          defaultFilter?.split("=")[0] !== value.field /* See also `ProductListProvider.handleResponse()` */ &&
          (value.type === "bool" ||
            value.values.size > 1 ||
            !isVariantsCategoryType) /* By now, most of the variants pages are manufacturer-, or commodity-group-specific */,
      );

      let filterItemsSorted: IProductListFilterItem[] = [];
      if (filtersToShow.length) {
        filtersToShow.forEach((filterString) => {
          const filterItem = filterItemsUnsorted.find((fi) => fi.field === filterString);
          if (filterItem) {
            filterItemsSorted.push(filterItem);
          }
        });
      } else {
        filterItemsSorted = filterItemsUnsorted;
      }

      filterItemsSorted.forEach((filterItem) => {
        switch (filterItem.type) {
          case ProductListFilterItemType.select:
            if (filterItem.field === "top_brand") {
              break;
            }
            tempFilterItems.push(
              <ProductFilterSelect
                item={filterItem as ProductListFilterItemSelect}
                key={filterItem.field}
                onSelect={(values) => {
                  if (isMobile) {
                    let newMobileFilters = Object.assign({}, mobileFilters);
                    if (!values.length) {
                      delete newMobileFilters[filterItem.field];
                    } else {
                      newMobileFilters = {...mobileFilters, [filterItem.field]: values};
                    }
                    setMobileFilters(newMobileFilters);
                    setIsDirty(true);
                  }
                }}
              />,
            );
            break;
          case ProductListFilterItemType.boolean:
            tempBooleanFilterItems.push(
              <ProductFilterBoolean
                item={filterItem as ProductListFilterItemBoolean}
                key={filterItem.field}
                onSelect={(value) => {
                  if (isMobile) {
                    let newMobileFilters = Object.assign({}, mobileFilters);
                    if (!value) {
                      delete newMobileFilters[filterItem.field];
                    } else {
                      newMobileFilters = {...mobileFilters, [filterItem.field]: value};
                    }
                    setMobileFilters(newMobileFilters);
                    setIsDirty(true);
                  }
                }}
              />,
            );
            break;
          case ProductListFilterItemType.range:
            if (filterItem.field === "percentageDiscount") {
              tempFilterItems.push(
                <ProductFilterRangeAsSelect
                  item={filterItem as ProductListFilterItemRange}
                  key={filterItem.field}
                  onSelect={(value) => {
                    if (isMobile) {
                      let newMobileFilters = Object.assign({}, mobileFilters);
                      if (!value) {
                        delete newMobileFilters[filterItem.field];
                      } else {
                        newMobileFilters = {...mobileFilters, [filterItem.field]: value};
                      }
                      setMobileFilters(newMobileFilters);
                      setIsDirty(true);
                    }
                  }}
                />,
              );
            } else {
              tempFilterItems.push(
                <ProductFilterRange
                  item={filterItem as ProductListFilterItemRange}
                  key={filterItem.field}
                  onSelect={(value) => {
                    if (isMobile) {
                      let newMobileFilters = Object.assign({}, mobileFilters);
                      const limit = state.rangeFilterMap?.get(filterItem.field);
                      if (value.max === limit?.max && value.min === limit?.min) {
                        delete newMobileFilters[filterItem.field];
                      } else {
                        newMobileFilters = {...mobileFilters, [filterItem.field]: value};
                      }
                      setMobileFilters(newMobileFilters);
                      setIsDirty(true);
                    }
                  }}
                />,
              );
            }
            break;
        }
      });
    }

    return [tempFilterItems, tempBooleanFilterItems];
  };

  // pre-call of the filterItemsArray() function, just to get the length of the result
  const [filterItems, booleanFilterItems] = filterItemArray();
  const filterLength = filterItems.length + booleanFilterItems.length;
  const matchingItems = filterLength > 0 || state.loading;

  // mobile layout
  if (isMobile) {
    const reset = () => {
      dispatch({type: "RESET"});
      dispatch({type: "TRIGGER"});
      setMobileFilters({});
      setDrawerOpen(false);
    };

    const setFilters = () => {
      if (!state.loading) {
        dispatch({
          type: "SET_LIMIT",
          payload: ProductList.PRODUCT_LIST_DEFAULT_LIMIT,
        });
        dispatch({
          type: "SET_FILTERS",
          payload: {filters: mobileFilters, doUrlParamUpdate: true},
        });
        if (isDrawerOpen) {
          setDrawerOpen(false);
        }
      }
    };

    return (
      <>
        {isSearchPage && !state.loading && <SearchHeadline />}
        <div className="column-8 filter-list--sticky">
          <div ref={filterHeadRef} className="filter-list__sort-section">
            <div>{t("product.items--quantity", {quantity: state.total})}</div>
            <div className="filter-elements__toggle-btn bold" onClick={toggleFilter}>
              <i className="icon icon-filter icon-size--22 mr-3" />
              <span>{t("product.sortAndFilter") + (filterNumbers() > 0 ? ` (${filterNumbers()})` : "")}</span>
            </div>
          </div>
        </div>
        <div className="column-8">
          <Drawer open={isDrawerOpen} onClose={toggleFilter}>
            <div className="filter-list p-5">
              {!isSearchPage && (
                <>
                  <div className="filter-list__title bold mb-3">{t("product.sortBy")}</div>
                  <ProductSortInput />
                </>
              )}
              <div className="filter-list__title bold mb-3">{t("product.useFilter")}</div>

              <div className="filter-elements">
                <div className="filter-elements__container">
                  <div className="bt-default" />
                  {[...filterItems, ...booleanFilterItems]}
                </div>
                <div className="filter-elements__buttons">
                  <button className="cta-button cta-green mx-0 my-3" onClick={setFilters} disabled={!isDirty}>
                    {state.loading ? <LoadingSpinner /> : <span>{t("product.applyFilter")}</span>}
                  </button>
                  <button className="cta-button cta-inverse mx-0 my-3" onClick={reset}>
                    {t("product.resetAndClose")}
                  </button>
                </div>
              </div>
            </div>
          </Drawer>
          <SelectedFilterOverview />
        </div>
        <div ref={productListRef} className="column-8 product-list__wrapper">
          <ListItems />
        </div>
      </>
    );
  }

  // desktop layout
  return (
    <>
      {isSearchPage && !state.loading && <SearchHeadline />}
      <div className={openFilter ? "column-m-3" : "column-8 column-l-12 filter-list--sticky"}>
        <div className={`filter-list ${openFilter ? "filter-list--sticky" : ""}`}>
          <div ref={filterHeadRef} className="filter-list__head">
            <div className="filter-list__sort-section">
              {matchingItems && (
                <div className="filter-elements__toggle-btn" onClick={toggleFilter}>
                  <i className="icon icon-filter icon-size--22 mr-3" />
                  <span>{openFilter ? t("product.hideFilter") : t("product.showFilter")}</span>
                </div>
              )}
              {filterLength === 0 && !state.loading && <BackButton />}
              {!isSearchPage && (
                <div className="filter-sort">
                  {matchingItems ? (
                    <>
                      <div className="total-item-count">
                        {t("product.items--quantity", {quantity: state.total})}
                        {" " + t("product.sortedBy")}
                      </div>
                      <ProductSortInput />
                    </>
                  ) : (
                    <div className="total-item-count">{t("product.items--quantity", {quantity: state.total})}</div>
                  )}
                </div>
              )}
            </div>
            <div
              className={`filter-list__reset-section ${openFilter && matchingItems ? "grid" : ""}`}
              ref={filterOverviewRef}
            >
              <SelectedFilterOverview />
            </div>
          </div>
          {openFilter && matchingItems && (
            <div className="filter-elements">
              <div className="filter-elements__container">
                <div className="bt-default" />
                {filterItems}
                {booleanFilterItems}
              </div>
            </div>
          )}
        </div>
      </div>
      <div
        ref={productListRef}
        className={[
          "column-8",
          openFilter && matchingItems ? "column-m-5 column-l-9 pl-3 open-filter" : "column-l-12",
        ].join(" ")}
      >
        <FilterOverviewPlaceholder />
        <div className="product-list__wrapper">
          <ListItems />
        </div>
      </div>
    </>
  );
};

export default ProductListFilter;

const BackButton: React.FC = () => {
  const {t} = useTranslation();

  return (
    <div className="filter-list__back-btn">
      <span className="link link-icon" onClick={() => window.history.back()}>
        <i className="icon-left--blue mr-3"></i>
        {t("general.back")}
      </span>
    </div>
  );
};
